import React, { useEffect, useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { LoadingIndicator } from "components/general/LoadingIndicator"
import { ModalConfirmAction } from "components/modals/ModalConfirmAction"

import { AdminBonus2Service } from "services/bonus/AdminBonus2Service"
import { AdminBonus2Simple, PromoCodeBonus, PromoCodeWriteFailure } from "services/bonus/type"

import { CreatePromoCodeModal } from "./CreatePromoCodeModal"
import { PromoCodeTable } from "./PromoCodeTable"
import { PromoCodeUsageModal, PromoCodeUsageStats } from "./PromoCodeUsageModal"

interface Props {
  tenant: string
}

export const PromoCodeManager = ({ tenant }: Props) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [promoCodes, setPromoCodes] = useState<PromoCodeBonus[]>([])
  const [bonuses, setBonuses] = useState<AdminBonus2Simple[]>([])
  const [loading, setLoading] = useState(false)
  const [showArchived, setShowArchived] = useState(false)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [selectedUsage, setSelectedUsage] = useState<PromoCodeUsageStats | null>(null)
  const [toggleTarget, setToggleTarget] = useState<{ promoCode: PromoCodeBonus; activate: boolean } | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<PromoCodeBonus | null>(null)

  useEffect(() => {
    loadData()
  }, [tenant, showArchived])

  const loadData = async () => {
    setLoading(true)
    const [codesData, bonusesData] = await Promise.all([
      AdminBonus2Service.listPromoCodes(tenant, showArchived),
      AdminBonus2Service.listBonuses(tenant, true)
    ])
    if (codesData) setPromoCodes(codesData)
    // Every active bonus is offered: a bonus may hold several codes, so one that already has
    // some is still a valid target for another.
    if (bonusesData) setBonuses(bonusesData)
    setLoading(false)
  }

  const handleCreate = async (bonusId: string, codes: string[]): Promise<PromoCodeWriteFailure | null> => {
    const result = await AdminBonus2Service.createPromoCodes(bonusId, codes, tenant)
    if (result.ok) {
      toast.success(t("bonus2.promo.toastCreated") as string)
      setShowCreateForm(false)
      loadData()
      return null
    }
    // Nothing was written — the modal marks the offending rows and the operator edits those.
    return result
  }

  const handleToggle = async (target: { promoCode: PromoCodeBonus; activate: boolean }) => {
    const { promoCode, activate } = target
    const success = await AdminBonus2Service.togglePromoCode(promoCode.id, activate, tenant)
    if (success) {
      toast.success((activate ? t("bonus2.promo.toastReactivated") : t("bonus2.promo.toastDeactivated")) as string)
      loadData()
    } else {
      toast.error((activate ? t("bonus2.promo.toastReactivateFailed") : t("bonus2.promo.toastDeactivateFailed")) as string)
    }
    setToggleTarget(null)
  }

  const handleArchive = async (promoCode: PromoCodeBonus, archived: boolean) => {
    const success = await AdminBonus2Service.archivePromoCode(promoCode.id, archived, tenant)
    if (success) {
      toast.success((archived ? t("bonus2.promo.toastArchived") : t("bonus2.promo.toastUnarchived")) as string)
      loadData()
    } else {
      toast.error(t("bonus2.promo.toastArchiveFailed") as string)
    }
  }

  const handleDelete = async (promoCode: PromoCodeBonus) => {
    const result = await AdminBonus2Service.deletePromoCode(promoCode.id, tenant)
    setDeleteTarget(null)
    if (result.ok) {
      toast.success(t("bonus2.promo.toastDeleted") as string)
      loadData()
      return
    }
    toast.error((result.redeemed ? t("bonus2.promo.toastDeleteRedeemed") : t("bonus2.promo.toastDeleteFailed")) as string)
  }

  const handleViewUsage = async (promoCode: PromoCodeBonus) => {
    const usageData = await AdminBonus2Service.getPromoCodeUsage(promoCode.promo_code, tenant)
    if (usageData) {
      setSelectedUsage(usageData)
    } else {
      toast.error(t("bonus2.promo.toastUsageFailed") as string)
    }
  }

  if (loading) {
    return <LoadingIndicator text={t("bonus2.promo.loading") as string} />
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-4">
        <h3 className="text-xl font-bold text-white">{t("bonus2.promo.promoCodes")}</h3>
        <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-300">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            className="checkbox checkbox-primary checkbox-sm"
          />
          {t("bonus2.promo.showArchived")}
        </label>
        <button
          onClick={() => setShowCreateForm(true)}
          className="ml-auto btn btn-primary bg-transparent text-[rgba(169,78,206,1)] border-[rgba(169,78,206,0.8)] hover:bg-[rgba(169,78,206,0.7)] hover:border-transparent dark:text-white hover:text-white"
        >
          + {t("bonus2.promo.createPromoCode")}
        </button>
      </div>

      {showCreateForm && (
        <CreatePromoCodeModal bonuses={bonuses} tenant={tenant} onSubmit={handleCreate} onClose={() => setShowCreateForm(false)} />
      )}

      {selectedUsage && <PromoCodeUsageModal usageData={selectedUsage} onClose={() => setSelectedUsage(null)} />}

      <PromoCodeTable
        promoCodes={promoCodes}
        onViewUsage={handleViewUsage}
        onToggle={(promoCode, activate) => setToggleTarget({ promoCode, activate })}
        onArchive={handleArchive}
        onDelete={setDeleteTarget}
      />

      {toggleTarget && (
        <ModalConfirmAction
          data={toggleTarget}
          handler={() => setToggleTarget(null)}
          text={
            toggleTarget.activate
              ? (t("bonus2.promo.confirmReactivate", { code: toggleTarget.promoCode.promo_code }) as string)
              : (t("bonus2.promo.confirmDeactivate", { code: toggleTarget.promoCode.promo_code }) as string)
          }
          actionText={(toggleTarget.activate ? t("bonus2.config.reactivate") : t("bonus2.config.deactivate")) as string}
          actionHandler={handleToggle}
        />
      )}

      {deleteTarget && (
        <ModalConfirmAction
          data={deleteTarget}
          handler={() => setDeleteTarget(null)}
          text={t("bonus2.promo.confirmDelete", { code: deleteTarget.promo_code }) as string}
          actionText={t("bonus2.promo.delete") as string}
          actionHandler={handleDelete}
        />
      )}
    </div>
  )
}
